---
title: "Who Is MrBeast?"
slug: "who-is-mrbeast"
excerpt: ""
publishedAt: "2026-05-25"
updatedAt: "2026-05-25"
author: "maya-lestari"
category: "Biography"
tags:
  - youtube
  - philanthropy
  - influencers
  - entertainment
featuredImage: "https://www.northjersey.com/gcdn/authoring/authoring-images/2024/12/19/USAT/77088475007-2190631119.jpg?crop=4613,2596,x0,y239&amp;width=3200&amp;height=1801&amp;format=pjpg&amp;auto=webp"
status: "published"
draft: false
---

## Infobox

<InfoBox title="MrBeast">
<ul>
<li><strong>Full name:</strong> Jimmy Donaldson</li>
<li><strong>Born:</strong> May 7, 1999 (age 25)</li>
<li><strong>Nationality:</strong> American</li>
<li><strong>Occupation:</strong> YouTuber, entrepreneur, philanthropist</li>
<li><strong>Known for:</strong> Philanthropic YouTube videos, challenges, and businesses</li>
<li><strong>Net worth:</strong> Estimated $500 million (2024)</li>
<li><strong>YouTube channels:</strong> MrBeast (main channel), MrBeast Gaming, Beast Pharmacy, Beast Burger</li>
<li><strong>Website:</strong> mrbeast.com</li>
</ul>
</InfoBox>

## Overview

MrBeast rose to prominence through his innovative and often extravagant YouTube content, which includes large-scale philanthropic stunts, such as giving away millions of dollars, planting millions of trees, and funding scholarships. His videos frequently feature extreme challenges, generous giveaways, and collaborations with other popular creators. Beyond content creation, MrBeast has expanded into various business ventures, including a fast-food chain ([Beast Burger](//en.wikipedia.org/wiki/Beast_Burger "Beast Burger")), a gaming channel ([MrBeast Gaming](//en.wikipedia.org/wiki/MrBeast_Gaming "MrBeast Gaming")), and a pharmacy ([Beast Pharmacy](//en.wikipedia.org/wiki/Beast_Pharmacy "Beast Pharmacy")).

His approach to content creation has redefined the YouTube landscape, emphasizing high production value, charitable acts, and community engagement. MrBeast's influence extends beyond YouTube, as he has become a cultural icon among younger generations and a significant figure in the creator economy.

## History / Background

### Early Life and YouTube Beginning

Jimmy Donaldson was born on **May 7, 1999**, in [Kansas](//en.wikipedia.org/wiki/Kansas "Kansas"), but grew up primarily in [Greenville, North Carolina](//en.wikipedia.org/wiki/Greenville,_North_Carolina "Greenville, North Carolina"). He developed an early interest in computers and video games, which later led him to explore content creation. He began posting videos on YouTube in early 2012 under the username "*MrBeast6000*," initially focusing on [Minecraft](//en.wikipedia.org/wiki/Minecraft "Minecraft") and [Let's Play](//en.wikipedia.org/wiki/Let%27s_Play "Let's Play") videos.

His early content struggled to gain traction, and he faced numerous setbacks, including channel bans and limited viewership. However, he persisted, experimenting with different video formats and refining his approach to storytelling and engagement.

### Breakthrough and Rise to Prominence

MrBeast's breakthrough came in 2017 with the video "*Counting to 100,000*," where he counted to 100,000 in a single take. The video went viral, amassing millions of views and establishing him as a creator capable of producing engaging, high-stakes content. This success was followed by a series of viral challenges and philanthropic videos, including "*Last to Leave*" and "*Squid Game in Real Life*," which further solidified his reputation.

In 2018, he launched the "*MrBeast Burger*" virtual restaurant, partnering with local restaurants to offer exclusive menu items. This venture marked the beginning of his expansion into the business world, leveraging his massive online following to drive sales and brand recognition.

### Expansion and Business Ventures

In 2020, MrBeast launched [MrBeast Gaming](//en.wikipedia.org/wiki/MrBeast_Gaming "MrBeast Gaming"), a gaming-focused YouTube channel that quickly gained popularity for its high-quality [Shorts](//en.wikipedia.org/wiki/YouTube_Shorts "YouTube Shorts") and long-form content. The channel features collaborations with other gaming creators and has become one of the most-subscribed gaming channels on the platform.

In 2022, he introduced [Beast Pharmacy](//en.wikipedia.org/wiki/Beast_Pharmacy "Beast Pharmacy"), an online pharmacy offering prescription medications at discounted prices. The business reflects his commitment to making essential services more accessible while aligning with his philanthropic values.

MrBeast has also ventured into traditional business models, including sponsorships, merchandise, and partnerships with major brands. His ability to monetize his influence has contributed significantly to his estimated net worth of **$500 million** as of 2024.

## How It Works

### Content Creation

MrBeast's content is characterized by its high production value, large budgets, and emphasis on philanthropy. His videos often feature:

- **Extreme Challenges:** High-stakes competitions with significant cash prizes, such as "*Last to Leave*" or "*Squid Game in Real Life*."
- **Philanthropic Acts:** Large-scale giveaways, such as donating $10,000 to random strangers or funding college scholarships.
- **Collaborations:** Partnerships with other popular YouTubers, celebrities, and influencers to create engaging and shareable content.
- **Community Engagement:** Involving his audience in decision-making processes, such as voting for charity recipients or challenge participants.
 
His team, which includes editors, producers, and researchers, plays a crucial role in executing these complex productions. MrBeast is known for his hands-on approach, often participating in every aspect of the video creation process.

### Business Model

MrBeast's business model revolves around leveraging his massive online following to generate revenue through multiple streams:

- **Advertising Revenue:** Earnings from YouTube ads on his videos, which are monetized through the platform's Partner Program.
- **Sponsorships:** Partnerships with brands that align with his values, such as [Quidd](//en.wikipedia.org/wiki/Quidd "Quidd") and [Shopify](//en.wikipedia.org/wiki/Shopify "Shopify").
- **Merchandise:** Sales of branded apparel, accessories, and other merchandise through his online store.
- **Virtual Restaurants:** Revenue from *MrBeast Burger* and other virtual dining concepts.
- **Pharmacy:** Income from *Beast Pharmacy*, which offers discounted prescription medications.
- **Investments:** Strategic investments in startups and other ventures to diversify his income sources.

## Important Facts

<FactList>
<ul>
<li><strong>Philanthropy:</strong> MrBeast has donated over <strong>$100 million</strong> to various charitable causes, including food banks, disaster relief, and education initiatives.</li>
<li><strong>YouTube Records:</strong> He holds multiple YouTube records, including the most-disliked video (for a short period) and the most-viewed individual creator channel.</li>
<li><strong>Team Size:</strong> His team consists of over <strong>50 employees</strong>, including editors, producers, and researchers who help manage his content and business operations.</li>
<li><strong>Social Media Presence:</strong> In addition to YouTube, MrBeast has a significant following on [TikTok](//en.wikipedia.org/wiki/TikTok "TikTok"), [Instagram](//en.wikipedia.org/wiki/Instagram "Instagram"), and [Twitter](//en.wikipedia.org/wiki/Twitter "Twitter"), with millions of followers across platforms.</li>
<li><strong>Environmental Initiatives:</strong> He has pledged to plant <strong>20 million trees</strong> as part of his environmental activism, partnering with organizations like [TeamTrees](//en.wikipedia.org/wiki/TeamTrees "TeamTrees").</li>
<li><strong>Legal Challenges:</strong> MrBeast has faced legal issues, including lawsuits related to his business practices and content, though he has largely avoided major legal repercussions.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "2012", title: "MrBeast begins posting YouTube", description: "MrBeast begins posting YouTube videos under the username \"MrBeast6000.\"" },
  { date: "2017", title: "Breakthrough video \"*Counting", description: "Breakthrough video \"*Counting to 100,000*\" goes viral." },
  { date: "2018", title: "Launches *MrBeast Burger*, a", description: "Launches *MrBeast Burger*, a virtual restaurant concept." },
  { date: "2019", title: "Reaches 10 million YouTube", description: "Reaches 10 million YouTube subscribers." },
  { date: "2020", title: "Launches *MrBeast Gaming* chan", description: "Launches *MrBeast Gaming* channel and introduces YouTube Shorts." },
  { date: "2021", title: "Surpasses 50 million YouTube", description: "Surpasses 50 million YouTube subscribers and launches the \"*Beast Philanthropy*\" channel." },
  { date: "2022", title: "Introduces *Beast Pharmacy* an", description: "Introduces *Beast Pharmacy* and expands into international markets." },
  { date: "2023", title: "Launches \"*MrBeast Feastables", description: "Launches \"*MrBeast Feastables*\" chocolate bar and expands merchandise line." },
  { date: "2024", title: "Estimated net worth reaches", description: "Estimated net worth reaches $500 million; continues to expand business ventures and philanthropic efforts." }
]} />

## FAQ

<FAQ>
<details>
<summary>How did MrBeast get his name?</summary>
<p>Jimmy Donaldson adopted the username "MrBeast" early in his YouTube career, inspired by the idea of being a "beast" at content creation. The name has since become synonymous with his brand.</p>
</details>
<details>
<summary>What is MrBeast's net worth?</summary>
<p>As of 2024, MrBeast's estimated net worth is approximately <strong>$500 million</strong>, primarily derived from YouTube ad revenue, sponsorships, business ventures, and investments.</p>
</details>
<details>
<summary>How much does MrBeast donate?</summary>
<p>MrBeast has donated over <strong>$100 million</strong> to various charitable causes, including food banks, disaster relief, and education initiatives. His philanthropic efforts are a central aspect of his brand.</p>
</details>
<details>
<summary>What businesses does MrBeast own?</summary>
<p>MrBeast owns several businesses, including *MrBeast Burger*, *Beast Pharmacy*, *Feastables* (chocolate bars), and *MrBeast Gaming*. He also has partnerships with brands like *Shopify* and *Quidd*.</p>
</details>
<details>
<summary>How many employees does MrBeast have?</summary>
<p>MrBeast's team consists of over <strong>50 employees</strong>, including editors, producers, researchers, and business professionals who help manage his content and operations.</p>
</details>
<details>
<summary>What is MrBeast's most-viewed video?</summary>
<p>As of 2024, MrBeast's most-viewed video is "*Squid Game in Real Life!*" with over <strong>500 million views</strong>.</p>
</details>
<details>
<summary>Is MrBeast married?</summary>
<p>MrBeast has not publicly confirmed his marital status, and he maintains a private personal life outside of his public persona.</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>Notes 1.  "MrBeast's YouTube Channels Statistics". Social Blade. Retrieved 2024-05-10.</li>
<li>"MrBeast's Net Worth 2024". Forbes. Retrieved 2024-05-10.</li>
<li>"MrBeast's Philanthropy". The New York Times. Retrieved 2024-05-10.</li>
<li>"MrBeast's Business Ventures". Business Insider. Retrieved 2024-05-10.</li>
</ol>
</ReferenceList>

